import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Alert, CircularProgress, List, ListItemButton,
  ListItemText, Divider, Chip, Stack, IconButton, Tooltip, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import { useApiClient } from '../../config/axios';

interface ModelListRow {
  id: string;
  historyflag: boolean | null;
  pivotdefn: string | null;
  contentdimensionid: string | null;
  updated_at: string;
  field_count: string;
}

interface ModelField {
  name: string;
  type: string;
  defnid: string | null;
  defntype: string | null;
  behaviorparams: any;
  name_full: string | null;
  attr_prefix: string | null;
  attr: string | null;
  attr_suffix: string | null;
}

interface DataIndexDef {
  data_index: string;
  item_type: string;
  data_type: string | null;
  defn_type: string | null;
  defn_id: string | null;
}

interface ModelDetail {
  id: string;
  historyflag: boolean | null;
  pivotdefn: string | null;
  contentdimensionid: string | null;
  model: any;
  fields: ModelField[];
  dataindex_defs: DataIndexDef[];
}

interface ModelsPanelProps {
  viewName: string;
}

const emptyNewField: Partial<ModelField> = { name: '', type: 'Float' };

const ModelsPanel: React.FC<ModelsPanelProps> = ({ viewName }) => {
  const apiClient = useApiClient();
  const [models, setModels] = useState<ModelListRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ModelDetail | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editBuffer, setEditBuffer] = useState<Partial<ModelField>>({});
  const [saving, setSaving] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newField, setNewField] = useState<Partial<ModelField>>(emptyNewField);

  const [jsonOpen, setJsonOpen] = useState(false);

  // Fetch model list on view change
  useEffect(() => {
    if (!viewName) return;
    setListLoading(true);
    setSelectedId(null);
    setDetail(null);
    setError(null);
    apiClient
      .get(`/view-manager/views/${encodeURIComponent(viewName)}/models`)
      .then(r => setModels(r.data.models || []))
      .catch(e => setError(e?.response?.data?.error || e.message || 'Failed to load models'))
      .finally(() => setListLoading(false));
  }, [apiClient, viewName]);

  // Fetch detail when a model is selected
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    setEditingField(null);
    apiClient
      .get(`/view-manager/models/${encodeURIComponent(selectedId)}`)
      .then(r => setDetail(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message || 'Failed to load model detail'))
      .finally(() => setDetailLoading(false));
  }, [apiClient, selectedId]);

  const refreshDetail = useCallback(async () => {
    if (!selectedId) return;
    const r = await apiClient.get(`/view-manager/models/${encodeURIComponent(selectedId)}`);
    setDetail(r.data);
  }, [apiClient, selectedId]);

  const startEdit = (f: ModelField) => {
    setEditingField(f.name);
    setEditBuffer({ ...f });
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditBuffer({});
  };

  const saveEdit = async () => {
    if (!selectedId || !editingField) return;
    setSaving(true);
    setError(null);
    try {
      const { name: _n, ...updates } = editBuffer as any;
      await apiClient.patch(
        `/view-manager/models/${encodeURIComponent(selectedId)}/fields/${encodeURIComponent(editingField)}`,
        updates
      );
      await refreshDetail();
      cancelEdit();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteField = async (fieldName: string) => {
    if (!selectedId) return;
    if (!window.confirm(`Delete field "${fieldName}" from ${selectedId}?`)) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.delete(
        `/view-manager/models/${encodeURIComponent(selectedId)}/fields/${encodeURIComponent(fieldName)}`
      );
      await refreshDetail();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const addField = async () => {
    if (!selectedId || !newField.name || !newField.type) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.post(
        `/view-manager/models/${encodeURIComponent(selectedId)}/fields`,
        newField
      );
      await refreshDetail();
      setNewField(emptyNewField);
      setAddOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Add failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left: model list for this view */}
      <Paper elevation={0} sx={{ width: 320, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Models for {viewName}
          </Typography>
        </Box>
        {listLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : models.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">No models linked to this view.</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {models.map(m => (
              <React.Fragment key={m.id}>
                <ListItemButton
                  selected={selectedId === m.id}
                  onClick={() => setSelectedId(m.id)}
                >
                  <ListItemText
                    primary={m.id}
                    secondary={
                      <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                        <Chip size="small" label={`${m.field_count} fields`} sx={{ height: 18 }} />
                        {m.pivotdefn && <Chip size="small" label={`pivot: ${m.pivotdefn}`} variant="outlined" sx={{ height: 18 }} />}
                      </Stack>
                    }
                  />
                </ListItemButton>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Right: detail + edit */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

        {!selectedId ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1">Pick a model on the left to view its fields.</Typography>
          </Box>
        ) : detailLoading || !detail ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">{detail.id}</Typography>
              {detail.historyflag && <Chip size="small" label="history" color="info" />}
              {detail.pivotdefn && <Chip size="small" label={`pivot: ${detail.pivotdefn}`} variant="outlined" />}
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<CodeIcon />} onClick={() => setJsonOpen(true)}>
                View JSON
              </Button>
              <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={() => setAddOpen(true)}>
                Add Field
              </Button>
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Fields ({detail.fields.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>defnid</TableCell>
                    <TableCell>defntype</TableCell>
                    <TableCell>attr</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.fields.map(f => {
                    const isEditing = editingField === f.name;
                    return (
                      <TableRow key={f.name} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{f.name}</TableCell>
                        {isEditing ? (
                          <>
                            <TableCell>
                              <TextField size="small" value={editBuffer.type || ''} onChange={e => setEditBuffer({ ...editBuffer, type: e.target.value })} />
                            </TableCell>
                            <TableCell>
                              <TextField size="small" value={editBuffer.defnid || ''} onChange={e => setEditBuffer({ ...editBuffer, defnid: e.target.value })} />
                            </TableCell>
                            <TableCell>
                              <TextField size="small" value={editBuffer.defntype || ''} onChange={e => setEditBuffer({ ...editBuffer, defntype: e.target.value })} />
                            </TableCell>
                            <TableCell>
                              <TextField size="small" value={editBuffer.attr || ''} onChange={e => setEditBuffer({ ...editBuffer, attr: e.target.value })} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Save"><IconButton size="small" color="primary" onClick={saveEdit} disabled={saving}><SaveIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Cancel"><IconButton size="small" onClick={cancelEdit} disabled={saving}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{f.type}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: 'text.secondary' }}>{f.defnid || ''}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: 'text.secondary' }}>{f.defntype || ''}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: 'text.secondary' }}>{f.attr || ''}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit"><IconButton size="small" onClick={() => startEdit(f)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteField(f.name)} disabled={saving}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {detail.dataindex_defs.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Data-index defs ({detail.dataindex_defs.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Item Type</TableCell>
                        <TableCell>Data Type</TableCell>
                        <TableCell>Defn Type</TableCell>
                        <TableCell>Defn Id</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.dataindex_defs.map(d => (
                        <TableRow key={d.data_index} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{d.data_index}</TableCell>
                          <TableCell>{d.item_type}</TableCell>
                          <TableCell>{d.data_type || ''}</TableCell>
                          <TableCell>{d.defn_type || ''}</TableCell>
                          <TableCell>{d.defn_id || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Box>

      {/* Raw JSON dialog */}
      <Dialog open={jsonOpen} onClose={() => setJsonOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.id} — raw model</DialogTitle>
        <DialogContent>
          <Box component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            {JSON.stringify(detail?.model, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setJsonOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Add-field dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add field to {selectedId}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" required value={newField.name || ''} onChange={e => setNewField({ ...newField, name: e.target.value })} />
            <TextField label="Type" required value={newField.type || ''} onChange={e => setNewField({ ...newField, type: e.target.value })} />
            <TextField label="defnid" value={newField.defnid || ''} onChange={e => setNewField({ ...newField, defnid: e.target.value })} />
            <TextField label="defntype" value={newField.defntype || ''} onChange={e => setNewField({ ...newField, defntype: e.target.value })} />
            <TextField label="attr" value={newField.attr || ''} onChange={e => setNewField({ ...newField, attr: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={addField} disabled={saving || !newField.name || !newField.type}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModelsPanel;
